import {
    domainApi,
    type CfSetupStatus,
    type CreateDomainRequest,
    type Domain,
    type DomainPricingResponse,
    type DomainRegistrationInfo,
    type DomainRegistrationRecord,
    type DomainSearchResult,
    type DomainStatus,
    type DomainType,
    type PurchaseDomainRequest,
    type PurchaseDomainResponse,
    type RenewDomainRequest,
    type RenewDomainResponse,
    type SaveRegistrarKeyRequest,
    type SslStatus,
    type TldPricing,
    type ToggleAutoRenewResponse,
    type UpdateDomainRequest,
    type UserRegistrarKey,
    type VerifyRegistrarKeyResponse,
} from '@/api/domains';
import { getErrorMessage } from '@/utils/error-handling';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { toast } from 'sonner';
import { balanceKeys } from './use-balance';

// Re-export types
export type {
    CfSetupStatus,
    CreateDomainRequest,
    Domain,
    DomainPricingResponse,
    DomainRegistrationInfo,
    DomainRegistrationRecord,
    DomainSearchResult,
    DomainStatus,
    DomainType,
    PurchaseDomainResponse,
    RenewDomainResponse,
    SslStatus,
    TldPricing,
    ToggleAutoRenewResponse,
    UpdateDomainRequest,
    UserRegistrarKey,
    VerifyRegistrarKeyResponse
};

// =============================================================================
// Query Keys
// =============================================================================

export const domainKeys = {
  all: ['domains'] as const,
  lists: () => [...domainKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...domainKeys.lists(), { filters }] as const,
  details: () => [...domainKeys.all, 'detail'] as const,
  detail: (id: string) => [...domainKeys.details(), id] as const,
  dns: (id: string) => [...domainKeys.detail(id), 'dns'] as const,
  pricing: () => [...domainKeys.all, 'pricing'] as const,
  search: (query: string) => [...domainKeys.all, 'search', query] as const,
  registrations: (limit?: number, offset?: number) =>
    [...domainKeys.all, 'registrations', { limit, offset }] as const,
  registrationInfo: (id: string) => [...domainKeys.detail(id), 'registration-info'] as const,
  registrarKeys: () => [...domainKeys.all, 'registrar-keys'] as const,
}

// =============================================================================
// Domain CRUD Hooks
// =============================================================================

export function useDomains(filters?: { type?: DomainType; status?: DomainStatus }) {
  return useQuery({
    queryKey: domainKeys.list(filters || {}),
    queryFn: () => domainApi.list(filters),
    staleTime: 30 * 1000,
    // Auto-poll every 10s when any domain is in a pending state.
    // Poll every 30s for error-state domains (may recover after DNS fix).
    // Stops polling once all domains reach a terminal state (active/suspended).
    refetchInterval: (query) => {
      const domains = query.state.data
      if (!domains || domains.length === 0) return false
      const hasPending = domains.some(
        (d) =>
          d.status === 'pending_ssl' ||
          d.status === 'pending' ||
          d.sslStatus === 'pending',
      )
      if (hasPending) return 10_000
      const hasError = domains.some((d) => d.status === 'error')
      return hasError ? 30_000 : false
    },
  })
}

export function useDomain(id: string) {
  return useQuery({
    queryKey: domainKeys.detail(id),
    queryFn: () => domainApi.get(id),
    enabled: !!id && id !== 'new',
    // Auto-poll every 10s while the domain is in a provisioning state.
    // Poll every 30s for error-state domains (may recover after DNS fix).
    refetchInterval: (query) => {
      const domain = query.state.data
      if (!domain) return false
      const isPending =
        domain.status === 'pending_ssl' ||
        domain.status === 'pending' ||
        domain.sslStatus === 'pending'
      if (isPending) return 10_000
      return domain.status === 'error' ? 30_000 : false
    },
  })
}

export function useDomainDnsInstructions(id: string) {
  return useQuery({
    queryKey: domainKeys.dns(id),
    queryFn: () => domainApi.getDnsInstructions(id),
    enabled: !!id && id !== 'new',
  })
}

export function useCreateDomain() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateDomainRequest) => domainApi.create(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: domainKeys.lists() })
      toast.success(`Domain "${data.domain}" added`)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useUpdateDomain() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDomainRequest }) =>
      domainApi.update(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: domainKeys.detail(data.id) })
      queryClient.invalidateQueries({ queryKey: domainKeys.lists() })
      toast.success(`Domain "${data.domain}" updated`)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useDeleteDomain() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => domainApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: domainKeys.lists() })
      toast.success('Domain deleted')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useVerifyDomain() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => domainApi.verify(id),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: domainKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: domainKeys.lists() })
      if (data.dnsVerified) {
        if (data.domainStatus === 'active') {
          toast.success('Domain is fully active with valid SSL!')
        } else {
          toast.success('All DNS records verified! SSL certificate is being provisioned...', {
            duration: 6000,
          })
        }
      } else {
        const missing = data.records?.filter((r) => !r.verified).map((r) => r.name)
        toast.error(
          `DNS records incomplete: ${missing?.join(', ') || 'check required records'}`,
          { duration: 8000 },
        )
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useRenewSSL() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => domainApi.refreshSsl(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: domainKeys.detail(data.id) })
      queryClient.invalidateQueries({ queryKey: domainKeys.lists() })
      if (data.status === 'active') {
        toast.success('SSL certificate is active! Domain is ready to use.')
      } else if (data.sslDiagnostic) {
        toast.info(data.sslDiagnostic)
      } else {
        toast.info(
          'SSL validation re-triggered. Cloudflare may take 1-5 minutes to complete. The page will auto-update.'
        )
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useSetDefaultDomain() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => domainApi.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: domainKeys.lists() })
      toast.success('Default domain updated')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

// =============================================================================
// Pricing
// =============================================================================

/** TLD pricing — cached for 1 hour */
export function useDomainPricing() {
  return useQuery({
    queryKey: domainKeys.pricing(),
    queryFn: () => domainApi.getPricing(),
    staleTime: 60 * 60 * 1000, // 1 hour
  })
}

// =============================================================================
// Search (debounced)
// =============================================================================

/** Search domains for purchase with 500ms debounce */
export function useSearchDomains(query: string) {
  const [debouncedQuery, setDebouncedQuery] = React.useState(query)

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 500)
    return () => clearTimeout(timer)
  }, [query])

  return useQuery({
    queryKey: domainKeys.search(debouncedQuery),
    queryFn: () => domainApi.searchForPurchase(debouncedQuery),
    enabled: debouncedQuery.length >= 2,
    staleTime: 60 * 1000, // 1 minute
  })
}

// =============================================================================
// Purchase & Renewal
// =============================================================================

/** Purchase a domain — invalidates domains list + balance */
export function usePurchaseDomain() {
  const queryClient = useQueryClient()

  return useMutation<PurchaseDomainResponse, Error, PurchaseDomainRequest>({
    mutationFn: (data) => domainApi.purchase(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: domainKeys.lists() })
      queryClient.invalidateQueries({ queryKey: domainKeys.registrations() })
      queryClient.invalidateQueries({ queryKey: balanceKeys.balance() })
      queryClient.invalidateQueries({ queryKey: balanceKeys.transactions() })
      toast.success(`Domain "${res.domain}" purchased!`)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

/** Renew a purchased domain — invalidates domains list + balance */
export function useRenewDomain() {
  const queryClient = useQueryClient()

  return useMutation<
    RenewDomainResponse,
    Error,
    { id: string; data?: RenewDomainRequest }
  >({
    mutationFn: ({ id, data }) => domainApi.renewDomain(id, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: domainKeys.lists() })
      queryClient.invalidateQueries({ queryKey: domainKeys.registrations() })
      queryClient.invalidateQueries({ queryKey: balanceKeys.balance() })
      queryClient.invalidateQueries({ queryKey: balanceKeys.transactions() })
      toast.success(`Domain "${res.domain}" renewed!`)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

/** Toggle auto-renewal for a managed domain */
export function useToggleAutoRenew() {
  const queryClient = useQueryClient()

  return useMutation<
    ToggleAutoRenewResponse,
    Error,
    { id: string; autoRenew: boolean }
  >({
    mutationFn: ({ id, autoRenew }) => domainApi.toggleAutoRenew(id, autoRenew),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: domainKeys.lists() })
      const action = res.autoRenew ? 'enabled' : 'disabled'
      toast.success(`Auto-renewal ${action} for ${res.domain}`)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

// =============================================================================
// Registration History / Info
// =============================================================================

/** Paginated registration history */
export function useDomainRegistrations(limit = 20, offset = 0) {
  return useQuery({
    queryKey: domainKeys.registrations(limit, offset),
    queryFn: () => domainApi.getRegistrations(limit, offset),
    staleTime: 30 * 1000,
  })
}

/** NameSilo registration info for a specific domain */
export function useDomainRegistrationInfo(id: string) {
  return useQuery({
    queryKey: domainKeys.registrationInfo(id),
    queryFn: () => domainApi.getRegistrationInfo(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// =============================================================================
// BYOK Registrar Keys
// =============================================================================

/** List BYOK registrar keys — cached 30s */
export function useRegistrarKeys() {
  return useQuery({
    queryKey: domainKeys.registrarKeys(),
    queryFn: () => domainApi.listRegistrarKeys(),
    staleTime: 30 * 1000,
  })
}

/** Save (create/upsert) a registrar key */
export function useSaveRegistrarKey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: SaveRegistrarKeyRequest) => domainApi.saveRegistrarKey(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: domainKeys.registrarKeys() })
      toast.success('Registrar API key saved')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

/** Delete a registrar key */
export function useDeleteRegistrarKey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => domainApi.deleteRegistrarKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: domainKeys.registrarKeys() })
      toast.success('Registrar API key deleted')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

/** Verify/re-verify a registrar key */
export function useVerifyRegistrarKey() {
  const queryClient = useQueryClient()

  return useMutation<VerifyRegistrarKeyResponse, Error, string>({
    mutationFn: (id) => domainApi.verifyRegistrarKey(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: domainKeys.registrarKeys() })
      if (res.valid) {
        toast.success('Registrar API key verified')
      } else {
        toast.error(`Key verification failed: ${res.error ?? 'unknown error'}`)
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}
